import {
  encounterPractitionerTagsEnabled,
  encounterPractitionerTagOptions
} from './currentUserUtils'
import {
  condition as conditionUtils,
  conditions as conditionsUtils,
  hasExternal,
  hasKnown,
  hasPotential,
  withAddressedConditions
} from './conditionUtils'
import {
  ButtonOptions,
  EncounterConditionDisplay,
  EncounterConditionDisplayModified,
  User
} from '../authenticated/types'

/** *****************************************************************************
 * Provides utilities for managing practitioner tags
 */

type TagOptions = Record<string, ButtonOptions[]> | null

/**
 * @param tagOptions Practitioner tag options
 * @return True if tag options include options for external conditions
 */
export function hasExternalTagOptions(tagOptions: TagOptions): boolean {
  return tagOptions !== null && tagOptions.EXTERNAL && tagOptions.EXTERNAL.length > 0
}

/**
 * @param tagOptions Practitioner tag options
 * @return True if tag options include options for activity conditions
 */
export function hasActivityTagOptions(tagOptions: TagOptions): boolean {
  return tagOptions !== null && tagOptions.ACTIVITY && tagOptions.ACTIVITY.length > 0
}

/**
 * @param tagOptions Practitioner tag options
 * @return True if tag options include options for known conditions
 */
export function hasKnownTagOptions(tagOptions: TagOptions): boolean {
  return tagOptions !== null && tagOptions.KNOWN && tagOptions.KNOWN.length > 0
}

/**
 * @param tagOptions Practitioner tag options
 * @return True if tag options include options for potential conditions
 */
export function hasPotentialTagOptions(tagOptions: TagOptions): boolean {
  return tagOptions !== null && tagOptions.POTENTIAL && tagOptions.POTENTIAL.length > 0
}

/**
 * Test a group of conditions to determine if there are applicable practitioner
 * tag options for the conditions
 * @param tagOptions Practitioner tag options
 * @param conditions Group of conditions
 * @return True if conditions have tag options; false if not
 */
export function hasPractitionerTagOptions(
  tagOptions: TagOptions,
  conditions: EncounterConditionDisplayModified[]
): boolean {
  if (conditionsUtils.hasActivity(conditions) && hasActivityTagOptions(tagOptions)) {
    return true
  }
  if (hasKnown(conditions as unknown as EncounterConditionDisplay[]) && hasKnownTagOptions(tagOptions)) {
    return true
  }
  if (hasPotential(conditions as unknown as EncounterConditionDisplay[]) && hasPotentialTagOptions(tagOptions)) {
    return true
  }
  if (hasExternal(conditions as unknown as EncounterConditionDisplay[]) && hasExternalTagOptions(tagOptions)) {
    return true
  }
  return false
}

/**
 * Determines the correct practitioner options based on the condition type.
 *
 * If the condition is external this overrides the activity, known, potential
 * condition types
 *
 * It is a good practice to first verify that tag options are available for the
 * condition type with hasPractitionerTagOptions, before calling this function
 *
 * @param condition Condition entity
 * @param tagOptions Practitioner tag options
 * @return Practitioner tag options that correspond the the condition type or
 *         null if no matching options
 */
export function conditionTagOptions(
  condition: EncounterConditionDisplay,
  tagOptions: TagOptions
): ButtonOptions[] | null {
  if (conditionUtils.isExternal(condition)) {
    return hasExternalTagOptions(tagOptions) && tagOptions !== null
      ? tagOptions.EXTERNAL
      : null
  }
  if (conditionUtils.isActivity(condition as unknown as EncounterConditionDisplayModified)) {
    return hasActivityTagOptions(tagOptions) && tagOptions !== null
      ? tagOptions.ACTIVITY
      : null
  }
  if (conditionUtils.isKnown(condition)) {
    return hasKnownTagOptions(tagOptions) && tagOptions !== null
      ? tagOptions.KNOWN
      : null
  }
  if (conditionUtils.isPotential(condition)) {
    return hasPotentialTagOptions(tagOptions) && tagOptions !== null
      ? tagOptions.POTENTIAL
      : null
  }
  return null
}

/**
 * This is a kitchen sink function to determine if practitioner tags are
 * available for display. Checks:
 * - Practitioner tags enabled for tenant
 * - Practitioner tag options defined for tenant
 * - Tag options defined for the specified group of conditions
 * - If the group of conditions is an HCC display group, the group does not
 *   contain any addressed conditions. An HCC group is considered addressed
 *   if any condition in the group is addressed
 * @param currentUser Current user entity
 * @param conditions Group of conditions
 * @param isHccGroup True if the client intends to display conditions as HCC group
 * @return True if it looks like practitioner tags are available for display
 *         false if not
 */
export function practitionerTagDisplayCheck(
  currentUser: User,
  conditions: EncounterConditionDisplayModified[],
  isHccGroup: boolean
): boolean {
  const tagsEnabled = encounterPractitionerTagsEnabled(currentUser)
  const tagOptions = encounterPractitionerTagOptions(currentUser)
  const hasTagOptions = hasPractitionerTagOptions(tagOptions, conditions)
  const isHccAddressed = withAddressedConditions(conditions as unknown as EncounterConditionDisplay[]).length > 0
  if (isHccGroup === true) {
    return !!tagsEnabled && hasTagOptions && !isHccAddressed
  }
  return !!tagsEnabled && hasTagOptions
}
